import type { IncidentActivation, IncidentDetail, IncidentStep } from "../../client-messages/incidents";

/**
 * One step of an incident's timeline, worded for support. `ticketNo` names
 * the activation the step links to, where it has one.
 */
export interface DescribedIncidentStep {
  readonly title: string;
  readonly detail?: string;
  readonly ticketNo?: string;
}

/**
 * Words a step from `explainIncident` using only what the incident stored, so
 * the timeline cannot say anything the rule did not decide. Support wording:
 * it names tickets and notes, which a customer-facing view must not.
 */
export function describeIncidentStep(step: IncidentStep, incident: IncidentDetail): DescribedIncidentStep {
  switch (step.kind) {
    case "call": {
      const call = incident.calls.find((c) => c.noteId === step.noteId);
      const outbound = call?.direction !== "inbound";
      const party =
        call?.police ?
          outbound ? " to the police"
          : " from the police"
        : "";
      const on = call?.ticketNo === undefined ? ", outside any activation" : ` on activation ${call.ticketNo}`;
      return {
        title: `${outbound ? "Outbound" : "Inbound"} call${party}${on}`,
        ...(step.madeGenuine ?
          { detail: "The first outbound call: this made it a genuine incident." }
        : !outbound && { detail: "Extends the incident; an inbound call never starts one." }),
        ...(call?.ticketNo !== undefined && { ticketNo: call.ticketNo }),
      };
    }
    case "activationOpened": {
      const activation = incident.activations.find((a) => a.ticketNo === step.ticketNo);
      const details = [activation && whyIn(activation), activation?.technicalOverridden && OVERRIDDEN].filter(
        (text): text is string => typeof text === "string",
      );
      return {
        title: `Activation ${step.ticketNo} opened`,
        ...(details.length > 0 && { detail: details.join(" ") }),
        ticketNo: step.ticketNo,
      };
    }
    case "activationClosed": {
      const reason = incident.activations.find((a) => a.ticketNo === step.ticketNo)?.reason;
      return {
        title: `Activation ${step.ticketNo} closed`,
        ...(reason !== undefined && reason !== "" && { detail: `Cleared as ${reason}.` }),
        ticketNo: step.ticketNo,
      };
    }
    case "settled":
      return {
        title: "Settled",
        detail: "15 minutes without a call and every activation closed: nothing more can join it.",
      };
  }
}

const OVERRIDDEN = "Counted here although its type is technical.";

const whyIn = ({ link }: IncidentActivation): string => {
  if (link.kind === "called") return "A call was made on it.";
  const nearest =
    link.nearest.kind === "call" ?
      `the call on note ${link.nearest.noteId}`
    : `called activation ${link.nearest.ticketNo}`;
  const when =
    link.gapSeconds === 0 ?
      `Open at the same time as ${nearest}`
    : `Open within ${minutes(link.gapSeconds)} of ${nearest}`;
  return `${when}, so it is part of the incident.`;
};

const minutes = (seconds: number): string => `${String(Math.max(1, Math.round(seconds / 60)))} min`;
